import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Download, Check, Loader2 } from "lucide-react";
import {
  trackEvent,
  getVisitorId,
  getSessionId,
  getStoredUTM,
  getStoredClickIds,
} from "@/lib/tracking";

// The capture organ's one form.
//
// Three things it does that the footer's EmailCapture does not, and each of
// them is the difference between an opt-in that counts and one that does not:
//
//   1. CONSENT IS EXPLICIT AND UNTICKED. subscribers.consent_marketing defaults
//      to true at the database, which is fine for "notify me when the next book
//      lands" and not fine for a magnet that puts someone on a weekly letter.
//      The checkbox starts empty and the answer is recorded either way.
//
//   2. IT AWAITS THE WRITE. The footer form is fire-and-forget, which is right
//      for analytics and wrong here: scoreboard line 3 counts *verified*
//      opt-ins, so this form does not claim success until the row exists.
//
//   3. IT REPORTS THE DELIVERY HONESTLY. If there is a file, the reader gets it
//      immediately. If there is not, it says so in plain words rather than
//      promising an email this studio currently has no provider to send.
//
// Attribution: full UTM set + click IDs from first-party storage, so a
// subscriber row can be joined back to the surface that produced it. That join
// is the entire point — an opt-in whose origin is unknown teaches nothing.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function OfferCapture({ offer, sourceKey }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [delivery, setDelivery] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!EMAIL_RE.test(clean)) {
      setError("That doesn't look like an email address.");
      return;
    }
    if (!consent) {
      setError("Please tick the box so we know we may write to you.");
      return;
    }
    setError("");
    setState("sending");

    const utm = getStoredUTM();
    const clickIds = getStoredClickIds();

    // Fire the analytics event first and independently. If /api/offer/claim
    // fails we still want to know the intent happened — losing the durable row
    // is bad, losing the knowledge that someone tried is worse.
    trackEvent("email_signup", {
      title_id: offer.sku,
      slug: offer.id,
      offer_id: offer.id,
      surface: sourceKey || "offer",
      vertical: offer.vertical,
    });

    try {
      const res = await fetch("/api/offer/claim", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: clean,
          offer_id: offer.id,
          sku: offer.sku,
          consent_marketing: true,
          source_slug: sourceKey || offer.id,
          visitor_id: getVisitorId(),
          session_id: getSessionId(),
          utm_source: utm.utm_source,
          utm_medium: utm.utm_medium,
          utm_campaign: utm.utm_campaign,
          utm_content: utm.utm_content,
          utm_term: utm.utm_term,
          fbclid: clickIds.fbclid,
          gclid: clickIds.gclid,
          page_url: typeof window !== "undefined" ? window.location.href : undefined,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) {
        setState("error");
        setError(
          body.error === "invalid_email"
            ? "That address was rejected. Try another?"
            : "Something went wrong on our side. Try once more?"
        );
        return;
      }
      setDelivery(body.delivery || { kind: "pending" });
      setState("done");
      if (body.delivery?.kind === "download") {
        trackEvent("offer_delivered", {
          title_id: offer.sku,
          slug: offer.id,
          offer_id: offer.id,
          delivery: body.delivery.via,
        });
      }
    } catch {
      setState("error");
      setError("We couldn't reach the server. Check your connection and try again?");
    }
  };

  // ── Delivered ───────────────────────────────────────────────────────
  if (state === "done") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="card-shrine rounded-sm p-6 md:p-8"
      >
        <div className="flex items-start gap-3">
          <Check size={20} className="text-dharma-gold-deep mt-1 shrink-0" />
          <div className="min-w-0">
            {delivery?.kind === "download" ? (
              <>
                <p className="font-display text-xl md:text-2xl text-ink leading-tight">
                  It's yours.
                </p>
                <p className="font-serif text-ink-soft text-lg mt-2 leading-relaxed">
                  {offer.deliverable.label} — {offer.deliverable.format},{" "}
                  {offer.deliverable.size_label}.
                </p>
                <a
                  href={delivery.url}
                  className="btn-pill btn-pill-solid mt-5"
                  onClick={() =>
                    trackEvent("offer_download_click", {
                      title_id: offer.sku,
                      slug: offer.id,
                      offer_id: offer.id,
                    })
                  }
                >
                  <Download size={14} />
                  Download now
                </a>
                {delivery.expires_in_days && (
                  <p className="font-body text-xs text-ink-mute mt-3">
                    This link is yours alone and works for{" "}
                    {delivery.expires_in_days} days.
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="font-display text-xl md:text-2xl text-ink leading-tight">
                  You're on the list.
                </p>
                {/* Honest, not evasive. We do not say "check your inbox" — there
                    is no transactional provider wired to send anything, and a
                    promise we cannot keep costs more than a plain sentence. */}
                <p className="font-serif text-ink-soft text-lg mt-2 leading-relaxed">
                  {offer.deliverable.label} isn't cut yet. We'll write the moment
                  it is — and nothing before then.
                </p>
              </>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // ── The form ────────────────────────────────────────────────────────
  return (
    <form onSubmit={submit} className="max-w-xl">
      <div className="flex flex-col sm:flex-row gap-3 items-stretch">
        <label htmlFor="offer-email" className="sr-only">
          Your email address
        </label>
        <input
          id="offer-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          disabled={state === "sending"}
          className="flex-1 bg-transparent border-b border-dharma-gold/40 text-ink py-3 px-1 font-serif text-lg focus:outline-none focus:border-dharma-gold transition-colors disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          className="btn-pill btn-pill-solid whitespace-nowrap disabled:opacity-60"
        >
          {state === "sending" ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Sending
            </>
          ) : (
            <>
              Send it to me
              <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>

      <label className="flex items-start gap-2.5 mt-5 cursor-pointer group">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 h-4 w-4 shrink-0 accent-[#8E6630] cursor-pointer"
        />
        <span className="font-body text-xs text-ink-mute leading-relaxed group-hover:text-ink-soft transition-colors">
          Yes, write to me — one short letter a week about new books, songs and
          films. Unsubscribe in one click, any time.
        </span>
      </label>

      {error && (
        <p className="font-body text-xs text-saffron-fire mt-3" role="alert">
          {error}
        </p>
      )}

      <AnimatePresence>
        {state === "idle" && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="font-body text-[0.7rem] text-ink-mute/80 mt-4 leading-relaxed"
          >
            We never sell or share your address.{" "}
            <a href="/privacy.html" className="underline hover:text-dharma-gold-deep">
              How we handle it
            </a>
            .
          </motion.p>
        )}
      </AnimatePresence>
    </form>
  );
}
